import { Box, Button, Grid, Paper, TextField, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CancelConfirmDialog } from '../../../components';

// Types for Notifications Settings
export interface NotificationsSettingsData {
  emailList: string;
}

export interface NotificationsSettingsProps {
  initialData?: NotificationsSettingsData;
  onSave: (data: NotificationsSettingsData) => void;
  isSaving?: boolean;
  readOnly?: boolean;
}

const NotificationsSettings: React.FC<NotificationsSettingsProps> = ({
  initialData,
  onSave,
  isSaving = false,
  readOnly = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // State
  const [emailList, setEmailList] = useState<string>(initialData?.emailList || '');
  const [emailListError, setEmailListError] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);

  // Sync state from initialData only on initial mount or when switching manufacturers
  const prevInitialDataRef = React.useRef(initialData);
  useEffect(() => {
    // Only update if this is the first load (prevRef is null/undefined)
    // OR if the manufacturer/component changed (completely different data)
    // OR if initialData changed from a value to undefined/null (manufacturer with no settings)
    const isInitialLoad = !prevInitialDataRef.current;
    const dataChanged =
      prevInitialDataRef.current &&
      initialData &&
      prevInitialDataRef.current.emailList !== initialData.emailList;
    const dataCleared = prevInitialDataRef.current && !initialData;

    if (initialData && (isInitialLoad || dataChanged)) {
      setEmailList(initialData.emailList);
      setHasUnsavedChanges(false);
      prevInitialDataRef.current = initialData;
    } else if (dataCleared) {
      // Reset to default values when switching to a manufacturer with no saved settings
      setEmailList('');
      setEmailListError('');
      setHasUnsavedChanges(false);
      prevInitialDataRef.current = initialData;
    }
  }, [initialData]);

  // Handlers
  const handleEmailListChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmailList(value);
    setHasUnsavedChanges(true);

    // Validation
    validateEmailList(value);
  };

  const validateEmailList = (value: string): string => {
    // Field is optional, so empty value is valid
    if (!value || value.trim() === '') {
      setEmailListError('');
      return '';
    }

    // Check max length
    if (value.length > 200) {
      const error = 'Maximum character limit of 200 characters';
      setEmailListError(error);
      return error;
    }

    // Check if commas are used as separators (common mistake)
    if (value.includes(',') && value.includes('@')) {
      const error = 'Multiple email addresses must be separated by semicolons (;) not commas';
      setEmailListError(error);
      return error;
    }

    // If there are multiple @ symbols, check if semicolons are used properly
    const emailCount = (value.match(/@/g) || []).length;
    if (emailCount > 1) {
      const semicolonCount = (value.match(/;/g) || []).length;
      // If there are multiple emails but not enough semicolons, warn
      if (semicolonCount < emailCount - 1) {
        const error = 'Multiple email addresses must be separated by semicolons (;)';
        setEmailListError(error);
        return error;
      }
    }

    // Check for invalid separator patterns (spaces between emails without semicolons)
    // This checks if there's an @ followed by text, then space, then another @
    // which suggests emails separated by spaces
    const emailPattern = /\S+@\S+\.\S+/g;
    const matches = value.match(emailPattern);
    if (matches && matches.length > 1) {
      // If we found multiple email-like patterns, ensure they're separated by semicolons
      const parts = value.split(';').map(part => part.trim());
      const hasMultipleEmailsInOnePart = parts.some(part => {
        const emailMatches = part.match(emailPattern);
        return emailMatches && emailMatches.length > 1;
      });
      if (hasMultipleEmailsInOnePart) {
        const error = 'Multiple email addresses must be separated by semicolons (;)';
        setEmailListError(error);
        return error;
      }
    }

    setEmailListError('');
    return '';
  };

  const handleSave = () => {
    // Re-validate before saving
    const error = validateEmailList(emailList);

    // Check if there are any errors
    if (error) {
      return;
    }

    const saveData: NotificationsSettingsData = {
      emailList,
    };

    onSave(saveData);
    setHasUnsavedChanges(false);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      handleCancelConfirm();
    }
  };

  const handleCancelConfirm = () => {
    setEmailList(initialData?.emailList ?? '');
    setEmailListError('');
    setHasUnsavedChanges(false);
    setShowCancelDialog(false);
    navigate('/');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          Notification Settings
        </Typography>

        <Grid container spacing={3}>
          {/* Email List Row */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Email List for Orders Ready for Pickup:
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={emailList}
                onChange={handleEmailListChange}
                error={!!emailListError}
                helperText={emailListError || `${emailList.length}/200 characters`}
                placeholder="Enter email addresses separated by semicolons"
                disabled={readOnly}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={handleCancel} disabled={isSaving || readOnly}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasUnsavedChanges || !!emailListError || isSaving || readOnly}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      <CancelConfirmDialog
        open={showCancelDialog}
        onConfirm={handleCancelConfirm}
        onDismiss={() => setShowCancelDialog(false)}
        dialogTitle="Cancel Changes?"
        dialogContent="You have unsaved changes. Are you sure you want to leave? Your changes will be lost."
        continuePrompt="Stay"
        cancelPrompt="Leave"
      />
    </Box>
  );
};

export default NotificationsSettings;
